import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FollowButton } from './FollowButton';
import type { UserSearchResult } from '../types';
import * as socialApi from '../services/social-api';

export interface UserSearchProps {
  onUserSelect?: (user: UserSearchResult) => void;
}

/**
 * User search/discovery component for finding people to follow.
 */
export function UserSearch({ onUserSelect }: UserSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < 2) {
      setResults([]);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const data = await socialApi.searchUsers(searchQuery.trim());
      setResults(data.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      search(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Search users..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        inputProps={{ 'aria-label': 'Search users' }}
      />

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {isLoading && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!isLoading && results.length > 0 && (
        <List>
          {results.map((user) => (
            <ListItem
              key={user.id}
              button
              onClick={() => onUserSelect?.(user)}
              sx={{ borderRadius: 1 }}
            >
              <ListItemAvatar>
                <Avatar src={user.avatarUrl} alt={user.displayName} />
              </ListItemAvatar>
              <ListItemText
                primary={user.displayName}
                secondary={user.biography}
                secondaryTypographyProps={{ noWrap: true }}
              />
              <ListItemSecondaryAction>
                <FollowButton userId={user.id} initialIsFollowing={user.isFollowing} />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {!isLoading && query.trim().length >= 2 && results.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          No users found
        </Typography>
      )}
    </Box>
  );
}
